import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { AnimationElement, AnimationElementAttributes } from "../Components/Elements/Graphics/AnimationElement";
import { Attributes } from "../Utils/Attributes";
import { Tweens } from "../Components/Effects/Tweens";

/**
 * AnimationElement can play animations from spritesheets.
 * AnimationElement uses spritesheets generated from editor (Adobe Animate) with json definition.
 * This element doesnt work without style. Style for this class is AnimationStyle.
 * Example:
 *   <AnimationElement Class="AnimationElement" Style="AnimationStyle" X="0" Y="0" />
 * @class AnimationElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class AnimationElementExtended extends AnimationElement {
    // protected running : boolean = false

    public updateByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(AnimationElementAttributes.DEBUG) &&
                node.getAttribute(AnimationElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        // if (this.running) {
        //     return;
        // } else {
        //     this.setActive();
        // }

        if (node.hasAttribute(AnimationElementAttributes.TINT)) {
            if (node.getAttribute(AnimationElementAttributes.TINT) === "0") {
                this.tint = null;
            } else if (node.getAttribute(AnimationElementAttributes.TINT) === "dynamic") {
                this.tint = parseInt(AnimationElement.DynamicTint, 16);
            } else {
                this.tint = parseInt(node.getAttribute(AnimationElementAttributes.TINT), 16);
            }
        } else {
            this.tint = 0xffffff;
        }

        this.alpha = Attributes.assignFloatAttribute(node, AnimationElementAttributes.ALPHA, this.alpha);

        if (node.hasAttribute(AnimationElementAttributes.ANCHOR)) {
            let anchorPoints = node.getAttribute(AnimationElementAttributes.ANCHOR).split(";");

            if (anchorPoints.length === 1) {
                this.anchor.x = parseFloat(anchorPoints[0]);
                this.anchor.y = parseFloat(anchorPoints[0]);
            } else if (anchorPoints.length === 2) {
                this.anchor.x = parseFloat(anchorPoints[0]);
                this.anchor.y = parseFloat(anchorPoints[1]);
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.ROTATION)) {
            let rotation = parseFloat(node.getAttribute(AnimationElementAttributes.ROTATION));
            this.rotation = this.degrees2radians(rotation);
        }

        /**
         * todo Replace by AnimationElementAttributes
         */
        if (node.hasAttribute(AnimationElementAttributes.START_ROTATION)) {
            if (
                node.getAttribute(AnimationElementAttributes.START_ROTATION) ===
                AnimationElementAttributes.START_ROTATION
            ) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        this.scale.x = Attributes.assignFloatAttribute(node, AnimationElementAttributes.SCALE_X, this.scale.x);
        this.scale.y = Attributes.assignFloatAttribute(node, AnimationElementAttributes.SCALE_Y, this.scale.y);

        if (node.hasAttribute(AnimationElementAttributes.SCALE)) {
            if (node.getAttribute(AnimationElementAttributes.SCALE) === "BIRD") {
                let type = Math.floor(this.getRandom(0, 5.9));
                if (type === 1) {
                    this.scale.x = -0.5;
                    this.scale.y = 0.5;
                } else if (type === 2) {
                    this.scale.x = 0.5;
                    this.scale.y = 0.5;
                } else if (type === 3) {
                    this.scale.x = -0.75;
                    this.scale.y = 0.75;
                } else if (type === 4) {
                    this.scale.x = 0.75;
                    this.scale.y = 0.75;
                } else if (type === 5) {
                    this.scale.x = -1;
                    this.scale.y = 1;
                } else {
                    this.scale.x = 1;
                    this.scale.y = 1;
                }
                this.alpha = this.scale.y - 0.25;
            } else {
                this.scale.x = parseFloat(node.getAttribute(AnimationElementAttributes.SCALE));
                this.scale.y = parseFloat(node.getAttribute(AnimationElementAttributes.SCALE));
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.X)) {
            let values = node.getAttribute(AnimationElementAttributes.X).split(";");

            if (values.length === 1) {
                this.position.x = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = Math.round(this.getRandom(parseFloat(values[0]), parseFloat(values[1])));
                this.position.x = value;
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.Y)) {
            let values = node.getAttribute(AnimationElementAttributes.Y).split(";");

            if (values.length === 1) {
                this.position.y = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = Math.round(this.getRandom(parseFloat(values[0]), parseFloat(values[1])));
                this.position.y = value;
            }
        }
        // this.position.x = Attributes.assignIntAttribute(node, AnimationElementAttributes.X, this.position.x);
        // this.position.y = Attributes.assignIntAttribute(node, AnimationElementAttributes.Y, this.position.y);

        this.pivot.x = Attributes.assignIntAttribute(node, AnimationElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, AnimationElementAttributes.PIVOT_Y, this.pivot.y);
        this.visible = Attributes.assignBooleanAttribute(node, AnimationElementAttributes.VISIBLE, this.visible);

        if (
            node.hasAttribute(AnimationElementAttributes.CONTINUE) &&
            StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.CONTINUE))
        ) {
            this._continue = true;
            this._stop = false;
            this.playMe();
        } else {
            this._continue = false;
        }

        const play =
            (node.hasAttribute(AnimationElementAttributes.PLAY) &&
                StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.PLAY))) ||
            (node.hasAttribute(AnimationElementAttributes.START) &&
                StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.START)));

        if (play) {
            this._stop = false;
            this._reverse = false;
            if (
                node.hasAttribute(AnimationElementAttributes.REVERSE) &&
                StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.REVERSE))
            ) {
                this._reverse = StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.REVERSE));
                this.animationSpeed = this.animationSpeed * (this._reverse ? -1 : 1);
            }

            if (node.hasAttribute(AnimationElementAttributes.SPEED)) {
                this.animationSpeed =
                    parseFloat(node.getAttribute(AnimationElementAttributes.SPEED)) * (this._reverse ? -1 : 1);
            }

            if (node.hasAttribute(AnimationElementAttributes.DELAY)) {
                let range = node
                    .getAttribute(AnimationElementAttributes.DELAY)
                    .split(";")
                    .map((value) => parseInt(value));
                if (range.length > 1 && !isNaN(range[0]) && !isNaN(range[1])) {
                    this._delay = range[0] + Math.random() * (range[1] - range[0]);
                    if (this._delay < 0) {
                        this._delay = 0;
                    }
                } else {
                    if (!isNaN(range[0])) {
                        this._delay = range[0];
                    }
                }
            }

            if (node.hasAttribute(AnimationElementAttributes.START_FRAME)) {
                this._startFrame = parseInt(node.getAttribute(AnimationElementAttributes.START_FRAME));
            }

            // this.animationSpeed = this._style.speed * ((this._reverse) ? -1 : 1);

            if (this._delay > 0) {
                this._timerDelay.stop();
                this._timerDelay.start(this._delay);
            } else {
                this.playMe();
            }
        } else if (this._startFrame != null && this._endFrame == null) {
            this._stop = false;

            if (
                node.hasAttribute(AnimationElementAttributes.DELAY) &&
                parseInt(node.getAttribute(AnimationElementAttributes.DELAY))
            ) {
                this._delay = parseInt(node.getAttribute(AnimationElementAttributes.DELAY));
            }

            if (node.hasAttribute(AnimationElementAttributes.START_FRAME)) {
                this._startFrame = parseInt(node.getAttribute(AnimationElementAttributes.START_FRAME));
            }

            if (this._delay > 0) {
                this._timerDelay.stop();
                this._timerDelay.start(this._delay);
            } else {
                this.playMe();
            }
            // this.playMe();
        }

        if (node.hasAttribute(AnimationElementAttributes.STOP)) {
            this._timerDelay.stop();
            this._stop = true;
            if (this.playing) {
                let endFrame = parseInt(node.getAttribute(AnimationElementAttributes.STOP));
                this.playMe(isNaN(endFrame) ? -1 : endFrame);
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.RESET)) {
            this._stop = true;
            this._timerDelay.stop();
            let endFrame = parseInt(node.getAttribute(AnimationElementAttributes.RESET));
            this.playMe(isNaN(endFrame) ? -1 : endFrame);
        }

        this.runPositionTween(node);
        this.runScaleTween(node);
        this.runRotationTween(node);
        this.runAlphaTween(node);

        if (DEBUG) {
            /*this._printDebugInfo({
             'element': 'Animation',
             'style': this._style.node.nodeName,
             'currentFrame': this.currentFrame,
             'currentTime': this._currentTime,
             'start': this._startFrame,
             'end': this._endFrame,
             'x': this.position.x,
             'y': this.position.y
             }, node);*/
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        // if (this.running) {
        //     return false;
        // } else {
        //     this.setActive();
        // }

        Tweens.removeTweens(this, "position");

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        if (node.getAttribute(AnimationElementAttributes.SCALE) === "BIRD") {
            let positionTweenXRight: Array<number> = ArrayUtils.numberArray(node.getAttribute("PositionTweenXRight"));
            let positionTweenXLeft: Array<number> = ArrayUtils.numberArray(node.getAttribute("PositionTweenXLeft"));
            if (this.scale.x > 0) {
                positionTweenX = [positionTweenXRight[0], positionTweenXRight[1]];
            } else {
                positionTweenX = [positionTweenXLeft[0], positionTweenXLeft[1]];
            }
            let birdMove: number[] = [60, 400];
            // if (node.hasAttribute("BirdMoveY")) {
            //     birdMove = ArrayUtils.numberArray(node.getAttribute("BirdMoveY"));
            // }
            // let endY = this.position.y - birdMove[0] + Math.round(this.getRandom(0, birdMove[0]*2));
            // if (endY > birdMove[1]) {
            //     endY = birdMove[1];
            // }
            // positionTweenY = [this.position.y, endY];
        } else {
            let positionTweenPosition: string = Attributes.assignStringAttribute(
                node,
                AnimationElementAttributes.POSITION_TWEEN_POSITION,
                null,
            );
            if (positionTweenPosition == AnimationElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
                positionTweenX = ArrayUtils.numberArray(
                    node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_X),
                ).map((value) => value + this.position.x);
                positionTweenY = ArrayUtils.numberArray(
                    node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_Y),
                ).map((value) => value + this.position.y);
            } else if (positionTweenPosition == AnimationElementAttributes.POSITION_TWEEN_POSITION_VALUE.SMOOTH) {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_X));
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_Y));
                if (positionTweenX.length) {
                    positionTweenX[0] = this.position.x;
                }
                if (positionTweenY.length) {
                    positionTweenY[0] = this.position.y;
                }
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_X));
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_Y));
            }
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            AnimationElementAttributes.POSITION_TWEEN_EASE,
            null,
        );

        let tweenDuration: number = 120;
        if (node.getAttribute(AnimationElementAttributes.SCALE) === "BIRD") {
            if (this.scale.x != 0) {
                let value: number = Math.round(500 / Math.abs(this.scale.x) + 1400 + this.getRandom(100, 300));
                tweenDuration = value;
            } else {
                tweenDuration = 2000;
            }
        } else {
            let values = node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_DURATION).split(";");
            if (values.length === 1) {
                tweenDuration = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = Math.round(this.getRandom(parseFloat(values[0]), parseFloat(values[1])));
                tweenDuration = value;
            } else if (values.length === 3) {
                let value: number = Math.round(
                    parseFloat(values[2]) * this.getRandom(parseFloat(values[0]), parseFloat(values[1])),
                );
                tweenDuration = value;
            }
        }

        let tweenDelay: number = 0;
        if (node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(AnimationElementAttributes.POSITION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            AnimationElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_KEY_TIMES))
            : null;

        let bezierType: string;
        let bezierCurviness: number;
        let bezierPoints: Object;
        let bezierObject: Object;
        let bezierAutoRotate: boolean;

        if (node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_BEZIER_POINTS)) {
            bezierType = Attributes.assignStringAttribute(
                node,
                AnimationElementAttributes.POSITION_TWEEN_BEZIER_TYPE,
                "thru",
            );
            bezierCurviness = Attributes.assignIntAttribute(
                node,
                AnimationElementAttributes.POSITION_TWEEN_BEZIER_CURVINESS,
                1.0,
            );
            bezierPoints = this.formatPoints(
                node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_BEZIER_POINTS),
            );
            bezierAutoRotate = Attributes.assignBooleanAttribute(
                node,
                AnimationElementAttributes.POSITION_TWEEN_BEZIER_AUTOROTATE,
                false,
            );

            if (bezierType && bezierCurviness && bezierPoints) {
                bezierObject = {
                    type: bezierType,
                    curviness: bezierCurviness,
                    values: bezierPoints,
                    autoRotate: bezierAutoRotate ? ["x", "y", "rotation", Math.PI / 2, true] : false,
                };
            }
        }

        Tweens.positionTween(this, {
            bezier: bezierObject,
            tweenX,
            tweenY,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            keyTimes,
        });

        // Tweens.positionTween(this, {bezier: bezierObject, tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes,
        //     callbackType: 'onComplete',
        //     callbackFunction: this.setInactive,
        //     callbackScope: this
        // });
    }

    playMe(frame: number = -1): void {
        if (!this._continue || !this.playing) {
            if (this._stop) {
                if (this._reverse) {
                    this.gotoAndStop(this._endFrame);
                } else {
                    if (frame !== -1) {
                        this.gotoAndStop(frame);
                    } else {
                        this.gotoAndStop(this._startFrame);
                    }
                }
            } else {
                if (this._randomStartFrame && this._startFrame === 0 && this._endFrame != null) {
                    this._startFrame = Math.floor(Math.random() * this._endFrame);
                }

                if (this._reverse) {
                    this.gotoAndPlay(this._endFrame);
                } else {
                    this.gotoAndPlay(this._startFrame);
                }
            }
        }
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    // protected setInactive() {
    //     this.running = false;
    // }
    //
    // protected setActive() {
    //     this.running = true;
    // }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/AnimationElementExtended.ts
 **/